import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {LoggedInLayout} from "features/layouts/pages/logged-in";
import {connect} from "react-redux";
import {getPaymentDataStart} from "features/pages/payment/reducer";
import {getPaymentData, getPaymentDataErrorSelector, getPaymentDataLoading} from "features/pages/payment/selectors";
import {LoadingLogoPortal} from "features/components/loading-logo";
import Text from "features/components/text";
import {__} from "services/i18n";
import Title from "features/components/title";
import Button from "features/components/button";
import PaymentForm from "features/forms/payment";
import InvoiceForm from "features/forms/invoice";
import {setInvoice} from "store/reducer/payments/invoice/reducer";
import {invoiceDataSelector} from "store/reducer/payments/invoice/selectors";
import {paymentCardsSelector} from "store/reducer/payments/cards/selectors";
import {historyObject} from "historyObject";
import {doPayReset, doPayStart} from "store/reducer/payments/pay/reducer";
import {doPayErrorSelector, doPayLoading, doPaySuccessSelector} from "store/reducer/payments/pay/selectors";
import {ModalPortal} from "features/components/modal";
import {Icon} from "features/components/icon";
import {getProfileStart} from "store/reducer/user/profile/reducer";
import {OSA_COMMUNITY_WEBSITE} from "constants.js";
import {Analytics} from "services/analytics";

/* istanbul ignore next */
const mapStateToProps = state => ({
    loading: getPaymentDataLoading(state),
    error: getPaymentDataErrorSelector(state),
    data: getPaymentData(state),
    invoice: invoiceDataSelector(state),
    cards: paymentCardsSelector(state),
    pay_loading: doPayLoading(state),
    pay_error: doPayErrorSelector(state),
    pay_success: doPaySuccessSelector(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    getPaymentDataStart,
    setInvoice,
    doPayStart,
    doPayReset,
    getProfileStart
};
export const PaymentPage = (props) => {
    const {type, resource_id} = props.match.params;
    const {getPaymentDataStart, setInvoice, doPayStart, doPayReset, getProfileStart} = props;
    const {loading, error, data, invoice, cards, pay_loading, pay_error, pay_success} = props;

    React.useEffect(() => {
        getProfileStart();
        getPaymentDataStart({type, resource_id});
        Analytics.trackInitiateCheckout();
    }, [getPaymentDataStart, resource_id, type, getProfileStart])

    const goToKnight = useCallback(() => {
        window.open(OSA_COMMUNITY_WEBSITE, "_blank");
    }, []);

    const [payment, setPayment] = useState({valid: false});

    const isValid = useMemo(() => {
        return payment.valid && invoice.valid;
    }, [invoice, payment]);

    const goPay = useCallback(() => {
        doPayStart({type, resource_id, payment_method: payment, invoice});
    }, [doPayStart, payment, resource_id, type, invoice]);

    const renderContent = useCallback(() => {
        if (loading) return <LoadingLogoPortal />;
        if (error.inError) return <Text size={16} color={"danger"} align={"center"}>{__(error.error)}</Text>
        if(!data) return null;
        return <div className={"sidebar-wrapper"}>
            <div className="content">
                <div className="white-box">
                    <Title size={24}>{__("payment.title")}</Title>
                    <div className="payment-item">
                        <div className="row type-cost">
                            <Text size={16}>{__(`payment.type-${type}`)}:</Text>
                            <Title size={24}>€ {data.cost}</Title>
                        </div>
                        <div className="title-image">
                            <img src={data.image} alt={"payment"}/>
                            <Title size={18}>{data.title}</Title>
                        </div>
                    </div>
                    <PaymentForm savedCards={cards} onChange={setPayment} />
                    <InvoiceForm {...invoice} onChange={setInvoice} />
                </div>
                {pay_error.inError && <Text size={16} align={"center"} color={"danger"}>{__(pay_error.error)}</Text>}
                <div className="row buttons">
                    <Button title={""} onClick={() => historyObject.goBack()} type={"square"} icon={"arrow-left"} />
                    <Button title={__("payment.pay-btn", [{key: "[price]", value: data.cost}])} onClick={goPay} icon={"check-circle-outline"} status={pay_loading ? "loading" : "success"} disabled={!isValid} />
                </div>
            </div>
            <div className="sidebar">
                <div className="white-box become-knight">
                    <div className="top" />
                    <div className="bottom">
                        <Title size={34} align={"center"}>{__("payment.become-knight.title")}</Title>
                        <Text size={16} align={"center"}>{__("payment.become-knight.text")}</Text>
                        <Button title={__("payment.become-knight.button")} onClick={goToKnight} type={"accent"} icon={"arrow-right"} />
                    </div>
                </div>
            </div>
        </div>;
    }, [loading, error, data, type, cards, invoice, setInvoice, pay_error, goPay, pay_loading, isValid, goToKnight]);

    const paymentFinished = useCallback(() => {
        doPayReset();
        Analytics.trackPurchase(data.cost);
        historyObject.replace(`/${type}/${resource_id}`);
    }, [doPayReset, resource_id, type, data]);

    React.useEffect(() => {
        if(!!data && !data.requires_payment) {
            paymentFinished();
        }
    }, [data, paymentFinished]);

    const renderSuccessModal = useCallback(() => {
        if(!pay_success) return null;
        return <ModalPortal className="payment-success-modal" size={"small"}>
            <Icon>check-circle-fill</Icon>
            <Title size={24} align={"center"}>{__("payment.success.title")}</Title>
            <Text size={16} align={"center"}>{__("payment.success.text")}</Text>
            <Button title={__("buttons.next")} onClick={paymentFinished} type={"outlined"} icon={"arrow-circle-right"} />
        </ModalPortal>
    }, [pay_success, paymentFinished]);

    return <LoggedInLayout
        type={"light"}
        className={"payment-page"}
        breadcrumbs={[{title: "common.home-breadcrumb", link:"/"}, {title: "payment.breadcrumb"}, {title: "..."}]}>
        {renderContent()}
        {renderSuccessModal()}
    </LoggedInLayout>
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentPage);